import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const AddPersonnelModal = ({ roleType, onClose, onAdd, customRoles }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    status: 'Available',
    location: '',
    emergencyContact: '',
    emergencyPhone: '',
    skills: '',
    equipment: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'On Job', label: 'On Job' },
    { value: 'Off Duty', label: 'Off Duty' }
  ];

  const roleOptions = {
    technicians: [
      { value: 'Senior HVAC Technician', label: 'Senior HVAC Technician' },
      { value: 'Electrical Systems Specialist', label: 'Electrical Systems Specialist' },
      { value: 'Plumbing & Drainage Expert', label: 'Plumbing & Drainage Expert' },
      { value: 'Appliance Repair Technician', label: 'Appliance Repair Technician' },
      { value: 'HVAC Installation Specialist', label: 'HVAC Installation Specialist' },
      { value: 'General Technician', label: 'General Technician' }
    ],
    managers: [
      { value: 'Field Operations Manager', label: 'Field Operations Manager' },
      { value: 'Service Coordinator', label: 'Service Coordinator' },
      { value: 'Regional Manager', label: 'Regional Manager' },
      { value: 'Operations Manager', label: 'Operations Manager' }
    ],
    sales: [
      { value: 'Senior Sales Representative', label: 'Senior Sales Representative' },
      { value: 'Account Executive', label: 'Account Executive' },
      { value: 'Sales Manager', label: 'Sales Manager' },
      { value: 'Business Development Representative', label: 'Business Development Representative' }
    ]
  };

  // Merge default roles with custom roles
  const getAvailableRoles = () => {
    const defaultRoles = roleOptions?.[roleType] || [];
    const custom = customRoles || [];
    return [...defaultRoles, ...custom];
  };

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'name':
        if (!value?.trim()) {
          error = 'Full name is required';
        } else if (value?.trim()?.length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;
      case 'email':
        if (!value?.trim()) {
          error = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (!value?.trim()) {
          error = 'Phone number is required';
        } else if (!/^\+?[\d\s\-()]+$/?.test(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
      case 'role':
        if (!value?.trim()) {
          error = 'Role is required';
        }
        break;
      case 'emergencyPhone':
        if (value?.trim() && !/^\+?[\d\s\-()]+$/?.test(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation on change if field was touched
    if (touched?.[field]) {
      let error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let error = validateField(field, formData?.[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = ['name', 'email', 'phone', 'role'];

    fieldsToValidate?.forEach(field => {
      let error = validateField(field, formData?.[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Validate emergency phone if provided
    if (formData?.emergencyPhone) {
      let error = validateField('emergencyPhone', formData?.emergencyPhone);
      if (error) {
        newErrors.emergencyPhone = error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async () => {
    // Mark all required fields as touched
    const allTouched = {
      name: true,
      email: true,
      phone: true,
      role: true
    };
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Map roleType to database role enum
      const roleMap = {
        'technicians': 'technician',
        'managers': 'manager',
        'sales': 'salesperson'
      };

      // Map status to database format
      const statusMap = {
        'Available': 'available',
        'On Job': 'on_job',
        'Off Duty': 'off_duty'
      };

      // Insert personnel record with base role type and custom role name
      const { data: personnelData, error: personnelError } = await supabase
        ?.from('personnel')
        ?.insert({
          name: formData?.name,
          email: formData?.email,
          phone: formData?.phone,
          role: roleMap?.[roleType], // Store base role type (technician/manager/salesperson)
          custom_role_name: formData?.role, // Store custom role name (e.g., "HVAC Specialist")
          status: statusMap?.[formData?.status] || 'available',
          location: formData?.location || null,
          avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d99ddb00-1763291684442.png',
          avatar_alt: `Professional headshot of ${formData?.name}`,
          emergency_contact: formData?.emergencyContact || null,
          emergency_phone: formData?.emergencyPhone || null,
          jobs_completed: 0,
          rating: 0
        })
        ?.select()
        ?.single();

      if (personnelError) {
        console.error('Error adding personnel:', personnelError?.message);
        alert('Failed to add personnel. Please try again.');
        setLoading(false);
        return;
      }

      // Insert skills if provided (for technicians)
      if (roleType === 'technicians' && formData?.skills) {
        const skillsArray = formData?.skills?.split(',')?.map(s => s?.trim())?.filter(Boolean);
        if (skillsArray?.length > 0) {
          const skillsToInsert = skillsArray?.map(skill => ({
            personnel_id: personnelData?.id,
            skill: skill
          }));

          const { error: skillsError } = await supabase
            ?.from('personnel_skills')
            ?.insert(skillsToInsert);

          if (skillsError) {
            console.error('Error adding skills:', skillsError?.message);
          }
        }
      }

      // Insert equipment if provided (for technicians)
      if (roleType === 'technicians' && formData?.equipment) {
        const equipmentArray = formData?.equipment?.split(',')?.map(e => e?.trim())?.filter(Boolean);
        if (equipmentArray?.length > 0) {
          const equipmentToInsert = equipmentArray?.map(equip => ({
            personnel_id: personnelData?.id,
            equipment: equip
          }));

          const { error: equipmentError } = await supabase
            ?.from('personnel_equipment')
            ?.insert(equipmentToInsert);

          if (equipmentError) {
            console.error('Error adding equipment:', equipmentError?.message);
          }
        }
      }

      // Call parent callback to refresh data
      onAdd(personnelData);
      setShowSuccess(true);

      // Send email notification
      sendPersonnelNotification('personnel_added', {
        name: formData?.name,
        email: formData?.email,
        role: formData?.role
      });

      // Close modal after showing success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to add personnel:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendPersonnelNotification = async (type, personnelData) => {
    try {
      setEmailSending(true);
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-personnel-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            type: type,
            recipientEmail: 'admin@rigjockey.com',
            recipientName: 'Admin',
            personnelName: personnelData?.name,
            personnelEmail: personnelData?.email,
            role: personnelData?.role,
            roleType: roleType,
            changedBy: 'System Admin'
          })
        }
      );

      const result = await response?.json();
      if (result?.success) {
        console.log('Personnel notification sent successfully');
      } else {
        console.error('Failed to send personnel notification:', result?.error);
      }
    } catch (error) {
      console.error('Error sending personnel notification:', error);
    } finally {
      setEmailSending(false);
    }
  };

  const getRoleTypeLabel = () => {
    switch (roleType) {
      case 'technicians':
        return 'Technician';
      case 'managers':
        return 'Manager';
      case 'sales':
        return 'Sales Personnel';
      default:
        return 'Personnel';
    }
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} color="var(--color-success)" />
          </div>
          <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
            Personnel Added Successfully!
          </h3>
          <p className="text-muted-foreground">
            {formData?.name} has been added to the system.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Add New {getRoleTypeLabel()}
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-lg hover:bg-muted transition-smooth disabled:opacity-50"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          <div className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              onBlur={() => handleBlur('name')}
              error={errors?.name}
              required
              disabled={loading}
            />

            <Input
              label="Email Address"
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              onBlur={() => handleBlur('email')}
              error={errors?.email}
              required
              disabled={loading}
            />

            <Input
              label="Phone Number"
              type="tel"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              onBlur={() => handleBlur('phone')}
              error={errors?.phone}
              placeholder="(555) 123-4567"
              required
              disabled={loading}
            />

            <Select
              label="Role"
              options={getAvailableRoles()}
              value={formData?.role}
              onChange={(value) => {
                handleInputChange('role', value);
                handleBlur('role');
              }}
              error={errors?.role}
              placeholder="Select a role"
            />

            <Select
              label="Status"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleInputChange('status', value)}
            />

            {roleType === 'technicians' && (
              <Input
                label="Current Location"
                type="text"
                value={formData?.location}
                onChange={(e) => handleInputChange('location', e?.target?.value)}
                placeholder="e.g., Downtown District"
                disabled={loading}
              />
            )}

            <div className="border-t border-border pt-4 mt-6">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Emergency Contact
              </h3>
              <div className="space-y-4">
                <Input
                  label="Emergency Contact Name"
                  type="text"
                  value={formData?.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e?.target?.value)}
                  disabled={loading}
                />

                <Input
                  label="Emergency Contact Phone"
                  type="tel"
                  value={formData?.emergencyPhone}
                  onChange={(e) => handleInputChange('emergencyPhone', e?.target?.value)}
                  onBlur={() => handleBlur('emergencyPhone')}
                  error={errors?.emergencyPhone}
                  placeholder="(555) 123-4567"
                  disabled={loading}
                />
              </div>
            </div>

            {roleType === 'technicians' && (
              <div className="border-t border-border pt-4 mt-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Skills & Equipment
                </h3>
                <div className="space-y-4">
                  <Input
                    label="Skills (comma-separated)"
                    type="text"
                    value={formData?.skills}
                    onChange={(e) => handleInputChange('skills', e?.target?.value)}
                    placeholder="e.g., HVAC, Electrical, Plumbing"
                    disabled={loading}
                  />

                  <Input
                    label="Equipment (comma-separated)"
                    type="text"
                    value={formData?.equipment}
                    onChange={(e) => handleInputChange('equipment', e?.target?.value)}
                    placeholder="e.g., Multimeter, Tool Kit, Wrench Set"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              fullWidth
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Adding Personnel...' : `Add ${getRoleTypeLabel()}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPersonnelModal;